import {
  apiGetDocs,
  apiCreateDoc,
  apiRemoveDoc,
  apiLoadDoc,
  apiSaveDoc,
  apiRenameDoc,
  apiMoveDoc,
  apiUpdateDocLayout,
  apiUpdateDocMetadata,
  apiGetProjectsForDoc,
  apiDeleteDoc
} from '../api/docs';
import { debug } from './debug';
import { getChunks, prepareChunks, extractPlainText, applyChunkAttributes, DEFAULT_CHUNK_CONFIG } from './chunkHelpers';
import { serializeToMarkdown } from './markdownHelpers';
import { computeDocumentStats } from './statHelpers';
import { isMetadataOnlyChunkRefsContent, shouldBlockDestructiveSave } from './contentGuards';
import { duplicateInternalImagesForDocContent, reconcileOrphanImages } from './imageHelpers';
import { apiSaveDocChunks, apiDeleteDocChunks, apiLoadChunks, apiLoadChunksByHashes } from '../api/chunks';
import { openDocInTabs } from './tabHelpers';
import { apiGetProject, apiTouchProjectModified } from '../api/projects';
import { apiLoadDocContext, apiUpsertDocContext } from '../api/docContext';
import { createContentSignature, createVersion, getVersions, deleteVersion } from './versionHelpers';
import { docMapKey } from './mapKey';
import { getUntitledBaseName } from './renameHelpers';
import { ensureDocAnchorIds } from './diffHelpers';

const EMPTY_WRAPPED_CONTENT = {
  json: { type: 'doc', content: [] },
  html: ''
};

const CHUNK_LOAD_MAX_ATTEMPTS = 3;
const CHUNK_LOAD_BASE_DELAY_MS = 120;
const MAX_SUMMARY_STATE_HASHES = 64;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const buildJsonFromFallbackContent = (rawContent = '') => {
  const text = String(rawContent || '').replace(/\r\n/g, '\n').trim();
  if (!text) return { type: 'doc', content: [] };

  const paragraphs = text
    .split(/\n{2,}/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => ({
      type: 'paragraph',
      content: [{ type: 'text', text: part }]
    }));

  return {
    type: 'doc',
    content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph', content: [{ type: 'text', text }] }]
  };
};

const normalizeDocJsonAnchors = (docJson, docId) => {
  const normalized = ensureDocAnchorIds(docJson, { docId });
  return normalized?.json || docJson;
};

const resolveRefHash = (ref) => {
  if (typeof ref === 'string') return ref.trim();
  if (ref && typeof ref === 'object' && Object.prototype.hasOwnProperty.call(ref, 'chunk_hash')) {
    return String(ref.chunk_hash || '').trim();
  }
  if (ref && typeof ref === 'object' && Object.prototype.hasOwnProperty.call(ref, 'chunkHash')) {
    return String(ref.chunkHash || '').trim();
  }
  if (ref && typeof ref === 'object' && Object.prototype.hasOwnProperty.call(ref, 'hash')) {
    return String(ref.hash || '').trim();
  }
  return '';
};

const normalizeChunkRefs = (refs = []) => {
  return (Array.isArray(refs) ? refs : []).map(ref => ({
    ...ref,
    chunk_hash: resolveRefHash(ref)
  }));
};

const isLikelyChatDocumentJson = (docJson = null) => {
  const nodes = Array.isArray(docJson?.content) ? docJson.content : [];
  if (nodes.length === 0) return false;
  const nonPageNodes = nodes.filter((node) => node?.type !== 'page');
  if (nonPageNodes.length === 0) return false;
  return nonPageNodes.every((node) => String(node?.type || '').trim() === 'chatMessage');
};

const mapReusedChunkHashes = (preparedChunks = [], prevChunkRefs = [], options = {}) => {
  const allowIndexFallback = Boolean(options?.allowIndexFallback);
  const normalizedPrevRefs = normalizeChunkRefs(prevChunkRefs);
  const byContentHash = new Map();
  for (const ref of normalizedPrevRefs) {
    const contentHash = String(ref?.hash || '').trim();
    const refHash = String(ref?.chunk_hash || '').trim();
    if (!contentHash || !refHash) continue;
    if (!byContentHash.has(contentHash)) {
      byContentHash.set(contentHash, refHash);
    }
  }

  const canFallbackByIndex = allowIndexFallback && normalizedPrevRefs.length === (preparedChunks || []).length;

  return (preparedChunks || []).map((chunk, index) => {
    const contentHash = String(chunk?.hash || '').trim();
    const reusedHash = byContentHash.get(contentHash);
    const fallbackByIndex = canFallbackByIndex
      ? String(normalizedPrevRefs?.[index]?.chunk_hash || '').trim()
      : '';
    return {
      ...chunk,
      chunkHash: reusedHash || fallbackByIndex || chunk.chunkHash
    };
  });
};

const toChunkRefHashMap = (refs = []) => {
  const map = new Map();
  for (const ref of normalizeChunkRefs(refs)) {
    const chunkHash = String(ref?.chunk_hash || '').trim();
    const contentHash = String(ref?.hash || '').trim();
    if (!chunkHash) continue;
    map.set(chunkHash, contentHash || null);
  }
  return map;
};

const updateDocSummaryRefreshState = async ({
  docId,
  reason = 'manual',
  prevChunkRefs = [],
  nextChunkRefs = []
} = {}) => {
  const normalizedDocId = Number(docId);
  if (!Number.isFinite(normalizedDocId) || normalizedDocId <= 0) return;

  const prevMap = toChunkRefHashMap(prevChunkRefs);
  const nextMap = toChunkRefHashMap(nextChunkRefs);

  const changedChunkHashes = [];
  for (const [chunkHash, nextHash] of nextMap.entries()) {
    const prevHash = prevMap.get(chunkHash) || null;
    if (!prevHash || prevHash !== nextHash) {
      changedChunkHashes.push(chunkHash);
    }
  }

  const removedChunkHashes = [];
  for (const chunkHash of prevMap.keys()) {
    if (!nextMap.has(chunkHash)) {
      removedChunkHashes.push(chunkHash);
    }
  }

  if (changedChunkHashes.length === 0 && removedChunkHashes.length === 0) return;

  try {
    const existingContext = await apiLoadDocContext({ docId: normalizedDocId });
    const existingSummaryState = existingContext?.summaryState && typeof existingContext.summaryState === 'object'
      ? existingContext.summaryState
      : {};
    const nowIso = new Date().toISOString();

    await apiUpsertDocContext({
      docId: normalizedDocId,
      summaryState: {
        ...existingSummaryState,
        chunkSummaryRefresh: {
          requestedAt: nowIso,
          reason: String(reason || 'manual').trim() || 'manual',
          changedChunkCount: changedChunkHashes.length,
          changedChunkHashes: changedChunkHashes.slice(0, MAX_SUMMARY_STATE_HASHES),
          removedChunkCount: removedChunkHashes.length,
          removedChunkHashes: removedChunkHashes.slice(0, MAX_SUMMARY_STATE_HASHES),
          totalChunkCount: nextMap.size
        }
      }
    });
  } catch (error) {
    debug('saveDocContent', { type: 'warn' }, `summary refresh state update skipped docId=${normalizedDocId}: ${error?.message || error}`);
  }
};

export const buildDocFromContent = async ({
  projectId,
  parentId = null,
  userId,
  name = 'Untitled',
  docType = null,
  content,
  layout = null,
  itemsMap,
  setItemsMap
} = {}) => {
  if (!projectId || !userId) return null;

  const record = await apiCreateDoc(projectId, name, '', userId, parentId, docType);
  if (!record?.doc_id) return null;

  const newDoc = {
    id: record.doc_id,
    name: record.name ?? name,
    parent_id: record.parent_id ?? parentId,
    type: 'doc',
    docType: docType === 'chat' ? 'chat' : null
  };

  if (itemsMap && setItemsMap) {
    if (itemsMap instanceof Map) {
      const newMap = new Map(itemsMap);
      newMap.set(docMapKey(newDoc.id), newDoc);
      setItemsMap(newMap);
    } else {
      const newObj = { ...itemsMap, [docMapKey(newDoc.id)]: newDoc };
      setItemsMap(newObj);
    }
  }

  await saveDocContent(newDoc.id, content || EMPTY_WRAPPED_CONTENT, userId, null, projectId);
  if (layout) {
    await updateDocLayout(newDoc.id, layout);
  }

  return newDoc;
};

// Duplicate a document: create a new doc in the same project with the same content
export const duplicateDoc = async (docItem, projectId, userId, itemsMap, setItemsMap) => {
  debug('duplicateDoc', { type: 'audit', level: 1 }, docItem, projectId, userId);
  if (!docItem || !projectId || !userId) return null;

  try {
    const loadedDoc = await loadDocFromChunks(docItem.id);
    const content = loadedDoc?.content || EMPTY_WRAPPED_CONTENT;
    const newName = `${docItem.name || 'Untitled'} (copy)`;

    return await buildDocFromContent({
      projectId,
      parentId: docItem.parent_id ?? null,
      userId,
      name: newName,
      content,
      layout: loadedDoc?.layout ?? null,
      itemsMap,
      setItemsMap
    });
  } catch (err) {
    console.error('[docHelpers] duplicateDoc error', err);
    return null;
  }
};

// -----------------------------
// Doc Helpers
// -----------------------------

export const loadDocFromChunks = async (docId, data = null) => {
  if (!docId) {
    return { content: EMPTY_WRAPPED_CONTENT, layout: null, name: '', updated_at: null, content_json: null };
  }

  const sourceData = data || await apiLoadDoc(docId);
  const chunkRefs = sourceData?.content_json?.chunks;

  if (!Array.isArray(chunkRefs) || chunkRefs.length === 0) {
    return {
      content: EMPTY_WRAPPED_CONTENT,
      layout: sourceData?.layout || null,
      name: sourceData?.name || '',
      updated_at: sourceData?.updated_at || null,
      content_json: sourceData?.content_json || null
    };
  }

  const refHashes = Array.from(new Set(chunkRefs.map(resolveRefHash).filter(Boolean)));
  let chunksFromDB = [];
  let unresolvedIndexes = [];
  let orderedChunks = [];

  for (let attempt = 1; attempt <= CHUNK_LOAD_MAX_ATTEMPTS; attempt += 1) {
    chunksFromDB = await apiLoadChunksByHashes(refHashes);

    if (chunkRefs.length > 0 && chunksFromDB.length === 0) {
      const byDoc = await apiLoadChunks(docId);
      if (Array.isArray(byDoc) && byDoc.length > 0) {
        chunksFromDB = byDoc;
        debug('loadDocContent', { type: 'warn', level: 1 }, {
          docId,
          attempt,
          note: 'Recovered chunks via doc_id fallback after hash lookup returned none.',
          refCount: chunkRefs.length,
          byDocCount: byDoc.length
        });
      }
    }

    const chunkMap = new Map(
      (chunksFromDB || [])
        .filter(chunk => chunk?.chunk_hash !== null && chunk?.chunk_hash !== undefined)
        .map(chunk => [String(chunk.chunk_hash), chunk])
    );

    unresolvedIndexes = [];
    orderedChunks = [];
    for (let index = 0; index < chunkRefs.length; index += 1) {
      const ref = chunkRefs[index];
      const refHash = resolveRefHash(ref);
      const chunk = refHash ? chunkMap.get(String(refHash)) : null;

      if (!chunk && chunksFromDB.length === chunkRefs.length) {
        const positional = chunksFromDB[index];
        if (positional && Array.isArray(positional?.content_json?.content)) {
          orderedChunks.push({
            ...positional,
            chunkHash: positional.chunk_hash,
            content: positional.content_json?.content || []
          });
          continue;
        }
      }

      if (!chunk || !Array.isArray(chunk?.content_json?.content)) {
        unresolvedIndexes.push(index);
        continue;
      }

      orderedChunks.push({
        ...chunk,
        chunkHash: chunk.chunk_hash,
        content: chunk.content_json?.content || []
      });
    }

    if (orderedChunks.length > 0) {
      break;
    }

    if (attempt < CHUNK_LOAD_MAX_ATTEMPTS) {
      debug('loadDocContent', { type: 'warn' }, {
        docId,
        attempt,
        reason: 'chunk-refs-unresolved-retry',
        refCount: chunkRefs.length,
        refHashesCount: refHashes.length
      });
      const delayMs = CHUNK_LOAD_BASE_DELAY_MS * attempt;
      debug('loadDocContent', { type: 'log' }, {
        docId,
        attempt,
        delayMs,
        note: 'Retrying unresolved chunk refs with bounded backoff.'
      });
      await sleep(delayMs);
    }
  }

  const allNodes = orderedChunks.flatMap(chunk => chunk.content_json?.content || []);
  const reconstructedJson = {
    type: 'doc',
    content: allNodes
  };

  const { minChars, maxChars } = DEFAULT_CHUNK_CONFIG;
  const totalChars = chunkRefs.reduce((sum, ref) => sum + (ref?.char_length || 0), 0);
  const tooShortChunks = chunkRefs.filter(ref => (ref?.char_length || 0) < minChars);
  const tooLongChunks = chunkRefs.filter(ref => (ref?.char_length || 0) > maxChars);
  const missingRefs = chunkRefs.length - orderedChunks.length;

  const shortChunksNeedRefactor = totalChars > minChars && tooShortChunks.length > 0;
  const longChunksNeedRefactor = tooLongChunks.length > 0;
  const malformedRefs = chunkRefs.some(ref => !resolveRefHash(ref));

  if (shortChunksNeedRefactor || longChunksNeedRefactor || malformedRefs || missingRefs > 0) {
    debug('loadDocContent', { type: 'warn', level: 1 }, {
      docId,
      missingRefs,
      malformedRefs,
      unresolvedIndexes: unresolvedIndexes.slice(0, 10),
      outOfRangeCount: tooShortChunks.length + tooLongChunks.length,
      shortChunks: tooShortChunks.length,
      longChunks: tooLongChunks.length,
      docCharLength: totalChars,
      min: minChars,
      max: maxChars,
      note: 'Will be refactored on next save.'
    });
  }

  if (orderedChunks.length === 0) {
    const sourceJson = sourceData?.content_json;
    if (sourceJson && sourceJson.type === 'doc' && Array.isArray(sourceJson.content)) {
      const normalizedSourceJson = normalizeDocJsonAnchors(sourceJson, docId);
      debug('loadDocContent', { type: 'warn' }, {
        docId,
        reason: 'Chunk references unresolved; using docs.content_json fallback.'
      });
      return {
        content: {
          json: normalizedSourceJson,
          html: ''
        },
        layout: sourceData?.layout || null,
        name: sourceData?.name || '',
        updated_at: sourceData?.updated_at || null,
        content_json: sourceData?.content_json || null
      };
    }

    const fallbackRawContent = String(sourceData?.content || '').trim();
    if (fallbackRawContent) {
      const fallbackJson = normalizeDocJsonAnchors(buildJsonFromFallbackContent(fallbackRawContent), docId);
      debug('loadDocContent', { type: 'warn' }, {
        docId,
        reason: 'Chunk references unresolved; reconstructed from docs.content fallback.'
      });
      return {
        content: {
          json: fallbackJson,
          html: ''
        },
        layout: sourceData?.layout || null,
        name: sourceData?.name || '',
        updated_at: sourceData?.updated_at || null,
        content_json: sourceData?.content_json || null
      };
    }

    debug('loadDocContent', { type: 'error' }, {
      docId,
      reason: 'Chunk references present but none resolved from chunks table',
      refCount: chunkRefs.length,
      refHashesCount: refHashes.length,
      refHashSample: refHashes.slice(0, 5),
      refTypeSample: chunkRefs.slice(0, 5).map(ref => typeof ref),
      resolvedCount: 0,
      dbChunkCount: Array.isArray(chunksFromDB) ? chunksFromDB.length : 0
    });

    throw new Error(`Chunk content unavailable for doc ${docId}`);
  }

  const jsonWithAttributes = applyChunkAttributes(reconstructedJson, orderedChunks);
  const normalizedJson = normalizeDocJsonAnchors(jsonWithAttributes, docId);
  return {
    content: {
      json: normalizedJson,
      html: ''
    },
    layout: sourceData?.layout || null,
    name: sourceData?.name || '',
    updated_at: sourceData?.updated_at || null,
    content_json: sourceData?.content_json || null
  };
};

export const loadDocContent = async (docId) => {
  if (!docId) return { content: EMPTY_WRAPPED_CONTENT, layout: null, name: '', updated_at: null, content_json: null };
  try {
    const data = await apiLoadDoc(docId);
    return await loadDocFromChunks(docId, data);
  } catch (err) {
    console.error('[docHelpers] loadDocContent error', err);
    return {
      content: EMPTY_WRAPPED_CONTENT,
      layout: null,
      name: '',
      updated_at: null,
      content_json: null,
      __loadFailed: true,
      __errorCode: String(err?.code || '').trim() || 'DOC_LOAD_FAILED',
      __errorMessage: String(err?.message || 'Unable to load document content')
    };
  }
};

export const openDoc = (
  doc,
  tabs,
  setTabs,
  activeTabId,
  setActiveTabId,
  editorRef,
  mode = 'replace'
) => {
  debug('openDoc', { type: 'log' }, `docId=${doc?.id ?? 'n/a'} tabs=${tabs?.length || 0} activeTabId=${activeTabId ?? 'null'} mode=${mode}`);
  if (!doc || !setTabs || !setActiveTabId) return;

  const existingTab = tabs.find(t => t.docId === doc.id);
  if (existingTab) {
    if (existingTab.id !== activeTabId) setActiveTabId(existingTab.id);
    if (editorRef?.current?.setSelectedDocId) editorRef.current.setSelectedDocId(doc.id);
    return;
  }

  const { tabs: updatedTabs, activeTabId: newActiveTabId } =
    openDocInTabs(tabs, activeTabId, doc, mode);

  debug('openDoc.setTabs', { type: 'log' }, `docId=${doc.id} tabs=${updatedTabs.length} activeTabId=${newActiveTabId ?? 'null'}`);
  setTabs(updatedTabs);
  setActiveTabId(newActiveTabId);

  if (editorRef?.current?.setSelectedDocId) editorRef.current.setSelectedDocId(doc.id);
};

export const saveDocContent = async (docId, content, userId, title, projectId = null, saveMeta = null) => {
  if (!docId || !userId) {
    const error = new Error('saveDocContent requires docId and userId');
    error.code = 'INVALID_SAVE_CONTEXT';
    throw error;
  }

  const reason = String(saveMeta?.reason || 'manual').trim() || 'manual';
  const requireUserInteraction = Boolean(saveMeta?.requireUserInteraction);
  const userInteractionDetected = saveMeta?.userInteractionDetected !== false;
  const allowDestructiveEmptySave = Boolean(saveMeta?.allowDestructiveEmptySave);

  if (requireUserInteraction && !userInteractionDetected) {
    const error = new Error('Doc not saved: No user interaction detected.');
    error.code = 'NO_USER_INTERACTION';
    error.docId = docId;
    debug('saveDocContent', { type: 'warn' }, `Doc ${docId} not saved: No user interaction detected`);
    throw error;
  }

  try {
    const latestDoc = await apiLoadDoc(docId);

    if (shouldBlockDestructiveSave(content, latestDoc) && !allowDestructiveEmptySave) {
      const error = new Error('Blocked destructive save: attempted to overwrite non-empty document with empty content.');
      error.code = 'DESTRUCTIVE_SAVE_BLOCKED';
      error.docId = docId;
      throw error;
    }

    // Check if content is new format { json, html } or legacy string/object
    const isNewFormat =
      content &&
      typeof content === 'object' &&
      Object.prototype.hasOwnProperty.call(content, 'json') &&
      Object.prototype.hasOwnProperty.call(content, 'html');
    
    if (isNewFormat) {
      // New format: { json, html }
      content.json = normalizeDocJsonAnchors(content?.json, docId);
      const nodeCount = Array.isArray(content?.json?.content) ? content.json.content.length : 0;
      debug('saveDocContent', { type: 'log' }, `Doc ${docId} saved (${reason}), ${nodeCount} nodes.`);

      if (isMetadataOnlyChunkRefsContent(content?.json)) {
        const error = new Error('Blocked invalid save: metadata-only chunk refs cannot be saved as editor content.');
        error.code = 'INVALID_EDITOR_CONTENT';
        error.docId = docId;
        throw error;
      }

      if (projectId && content?.json && typeof content.json === 'object') {
        try {
          const duplicatedImages = await duplicateInternalImagesForDocContent({
            projectId,
            targetDocId: docId,
            contentJson: content.json,
            uploadedBy: userId || null
          });
          if (duplicatedImages?.contentJson) {
            content.json = duplicatedImages.contentJson;
          }
        } catch (copyErr) {
          debug('saveDocContent', { type: 'warn' }, `image self-containment skipped docId=${docId}: ${copyErr?.message || copyErr}`);
        }
      }

      const prevChunkRefs = latestDoc?.content_json?.chunks || [];
      const normalizedPrevChunkRefs = normalizeChunkRefs(prevChunkRefs);
      const prevRefHashes = Array.from(new Set(normalizedPrevChunkRefs.map(ref => ref.chunk_hash).filter(Boolean)));

      let prevChunkRows = [];
      if (prevRefHashes.length > 0) {
        prevChunkRows = await apiLoadChunksByHashes(prevRefHashes);
        if ((!Array.isArray(prevChunkRows) || prevChunkRows.length === 0) && docId) {
          const fallbackRows = await apiLoadChunks(docId);
          prevChunkRows = Array.isArray(fallbackRows) ? fallbackRows : [];
        }
      }

      const malformedPrevRefs = normalizedPrevChunkRefs.some(ref => !ref.chunk_hash);
      const missingReferencedChunks = prevRefHashes.length > 0 && (!Array.isArray(prevChunkRows) || prevChunkRows.length === 0);

      const needsJsonFix = malformedPrevRefs || missingReferencedChunks;

      if (needsJsonFix && projectId && userId) {
        try {
          await createVersion({
            projectId,
            docId,
            trigger: 'pre-fix-backup',
            snapshot: {
              document: {
                content: latestDoc?.content_json?.type === 'doc'
                  ? latestDoc.content_json
                  : (content?.json || EMPTY_WRAPPED_CONTENT.json),
                layout: latestDoc?.layout || null,
                updated_at: latestDoc?.updated_at || null
              }
            }
          });
        } catch (versionErr) {
          debug('saveDocContent', { type: 'warn' }, `pre-fix backup failed docId=${docId}: ${versionErr?.message || versionErr}`);
        }
      }
      
      // Extract chunks from the content based on chunk attributes (maintained by editor)
      const chunks = getChunks(content.json, {}, docId);
      
      const preparedChunksRaw = await prepareChunks(chunks);
      const preparedChunks = mapReusedChunkHashes(preparedChunksRaw, normalizedPrevChunkRefs, {
        allowIndexFallback: isLikelyChatDocumentJson(content?.json)
      });

      // Keep editor chunk attrs aligned with persisted/reused chunk hashes.
      const chunksWithStableHashes = chunks.map((chunk, index) => ({
        ...chunk,
        chunkHash: preparedChunks[index]?.chunkHash || chunk?.chunkHash
      }));

      // Apply chunk attributes to the content for future edits and compute doc-level MD
      content.json = applyChunkAttributes(content.json, chunksWithStableHashes);
      const docMd = serializeToMarkdown(content.json);

      // Store ordered array of chunk_hash + metrics for reconstruction
      const chunkRefs = preparedChunks.map(chunk => ({
        chunk_hash: chunk.chunkHash,
        char_length: chunk.char_length,
        hash: chunk.hash,
        content_json: chunk.content_json,
        content_md: chunk.content_md || null,
        splitReason: chunk.splitReason
      }));
      await apiSaveDocChunks(docId, chunkRefs, normalizedPrevChunkRefs, docMd);
      const totalLength = preparedChunks.reduce((sum, chunk) => sum + chunk.char_length, 0);
      const stats = computeDocumentStats(content.json, {
        chunks: chunkRefs.length,
        chars: totalLength
      });
      
      // Save doc with ordered chunk references in content_json (metadata only, no content)
      const contentJsonRefs = preparedChunks.map(chunk => ({
        chunk_hash: chunk.chunkHash,
        char_length: chunk.char_length,
        hash: chunk.hash
      }));
      const contentJson = {
        chunks: contentJsonRefs,
        stats
      };
      
      const savedDoc = await apiSaveDoc(docId, content.html, userId, {
        chunk_count: chunkRefs.length,
        content_json: contentJson
      });

      await updateDocSummaryRefreshState({
        docId,
        reason,
        prevChunkRefs: normalizedPrevChunkRefs,
        nextChunkRefs: contentJsonRefs
      });

      if (projectId && userId) {
        await apiTouchProjectModified(projectId, userId);
      }
      
      const wasRechunked = Boolean(needsJsonFix) || (preparedChunks.length !== prevChunkRefs.length);
      
      // Return both the saved doc and the updated content to avoid reloading
      return {
        ...savedDoc,
        updatedContent: {
          json: content.json,
          html: content.html
        },
        wasRechunked
      };
    } else {
      // Legacy flow: Save as string or object
      const isJson = typeof content === 'object' && content !== null;

      if (isJson) {
        if (isMetadataOnlyChunkRefsContent(content)) {
          const error = new Error('Blocked invalid save: metadata-only chunk refs cannot be saved as editor content.');
          error.code = 'INVALID_EDITOR_CONTENT';
          error.docId = docId;
          throw error;
        }

        // Canonicalize object content to the same chunk-ref format used by the
        // wrapped {json, html} branch so load/reload paths stay consistent.
        const jsonContent = normalizeDocJsonAnchors(content, docId);
        const htmlContent = extractPlainText(jsonContent);
        const chunks = getChunks(jsonContent, {}, docId);
        const prevChunkRefs = latestDoc?.content_json?.chunks || [];
        const normalizedPrevChunkRefs = normalizeChunkRefs(prevChunkRefs);
        const preparedChunksRaw = await prepareChunks(chunks);
        const preparedChunks = mapReusedChunkHashes(preparedChunksRaw, normalizedPrevChunkRefs, {
          allowIndexFallback: isLikelyChatDocumentJson(jsonContent)
        });

        const chunksWithStableHashes = chunks.map((chunk, index) => ({
          ...chunk,
          chunkHash: preparedChunks[index]?.chunkHash || chunk?.chunkHash
        }));

        // Apply chunk attributes and compute doc-level markdown for storage
        const appliedJson = applyChunkAttributes(jsonContent, chunksWithStableHashes);
        const docMd = serializeToMarkdown(appliedJson);

        const chunkRefsForStorage = preparedChunks.map(chunk => ({
          chunk_hash: chunk.chunkHash,
          char_length: chunk.char_length,
          hash: chunk.hash,
          content_json: chunk.content_json,
          content_md: chunk.content_md || null,
          splitReason: chunk.splitReason
        }));

        await apiSaveDocChunks(docId, chunkRefsForStorage, normalizedPrevChunkRefs, docMd);

        const contentJsonRefs = preparedChunks.map(chunk => ({
          chunk_hash: chunk.chunkHash,
          char_length: chunk.char_length,
          hash: chunk.hash
        }));

        const saved = await apiSaveDoc(docId, htmlContent, userId, {
          chunk_count: contentJsonRefs.length,
          content_json: {
            chunks: contentJsonRefs,
            stats: computeDocumentStats(jsonContent, {
              chunks: contentJsonRefs.length,
              chars: preparedChunks.reduce((sum, chunk) => sum + chunk.char_length, 0)
            })
          }
        });

        await updateDocSummaryRefreshState({
          docId,
          reason,
          prevChunkRefs: normalizedPrevChunkRefs,
          nextChunkRefs: contentJsonRefs
        });

        if (projectId && userId) {
          await apiTouchProjectModified(projectId, userId);
        }

        return {
          ...saved,
          updatedContent: {
            json: appliedJson,
            html: htmlContent
          }
        };
      }

      // Legacy string format
      const saved = await apiSaveDoc(docId, content, userId);
      if (projectId && userId) {
        await apiTouchProjectModified(projectId, userId);
      }
      return saved;
    }
  } catch (err) {
    console.error('[docHelpers] saveDocContent error', err);
    if (err?.code === 'INVALID_SAVE_CONTEXT' || err?.code === 'DESTRUCTIVE_SAVE_BLOCKED' || err?.code === 'INVALID_EDITOR_CONTENT' || err?.code === 'NO_USER_INTERACTION') {
      throw err;
    }

    const wrapped = new Error(err?.message || 'Failed to save document');
    wrapped.code = 'SAVE_FAILED';
    wrapped.saveFailure = {
      ok: false,
      code: 'SAVE_FAILED',
      message: err?.message || 'Failed to save document',
      retryable: true
    };
    wrapped.cause = err;
    throw wrapped;
  }
};

export const createDoc = async (projectId, parentId, userId, itemsMap, setItemsMap, docType = null) => {
  debug('createDoc', { type: 'log' }, `requested projectId=${projectId ?? 'n/a'} parentId=${parentId ?? 'null'} userId=${userId ?? 'n/a'}`);
  if (!projectId || !userId) return null;
  try {
    // Verify project is accessible before creating.
    const project = await apiGetProject(projectId);
    if (!project) {
      console.warn('[createDoc] user not authorized to create doc in project', projectId, userId);
      return null;
    }
    // compute a unique name among all docs in the project
    const base = getUntitledBaseName(docType);
    let name = base;
    const normalizedDocType = docType === 'chat' ? 'chat' : null;
    try {
      const existingDocNames = [];

      // Use authoritative project docs so chat numbering survives tree refreshes.
      const projectDocs = await apiGetDocs(projectId);
      if (Array.isArray(projectDocs) && projectDocs.length > 0) {
        for (const doc of projectDocs) {
          const existingType = String(doc?.docType || doc?.type || '').trim().toLowerCase() || null;
          const isSameType = normalizedDocType === 'chat'
            ? existingType === 'chat'
            : existingType !== 'chat';
          if (isSameType && doc?.name) existingDocNames.push(doc.name);
        }
      }

      // Fallback when project docs are unavailable.
      if (existingDocNames.length === 0 && itemsMap) {
        const iter = itemsMap instanceof Map ? itemsMap.values() : Object.values(itemsMap);
        for (const it of iter) {
          const existingType = String(it?.docType || '').trim().toLowerCase() || null;
          const isSameType = normalizedDocType === 'chat'
            ? existingType === 'chat'
            : existingType !== 'chat';
          if (it?.type === 'doc' && isSameType && it?.name) existingDocNames.push(it.name);
        }
      }

      const { nextIndexedName } = await import('./projectHelpers');
      name = nextIndexedName(base, existingDocNames);
    } catch (e) {
      name = base;
    }

    const record = await apiCreateDoc(projectId, name, '', userId, parentId, normalizedDocType);
    if (!record) return null;

    const newDoc = {
      id: record.doc_id,
      name: name,  // Use the computed unique name
      parent_id: record.parent_id ?? parentId,
      type: 'doc',
      docType: normalizedDocType
    };

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const newMap = new Map(itemsMap);
        newMap.set(docMapKey(newDoc.id), newDoc);
        setItemsMap(newMap);
      } else {
        const newObj = { ...itemsMap, [docMapKey(newDoc.id)]: newDoc };
        setItemsMap(newObj);
      }
    }

    await apiTouchProjectModified(projectId, userId);

    debug('createDoc', { type: 'log' }, `created docId=${newDoc.id} name="${name}" parentId=${newDoc.parent_id ?? 'null'} projectId=${projectId}`);

    return newDoc;
  } catch (err) {
    console.error('[docHelpers] createDoc error', err);
    return null;
  }
};

export const renameDoc = async (docId, newName, itemsMap, setItemsMap, projectId = null, userId = null) => {
  debug(`renameDoc [doc:${docId}]`, { type: 'audit', level: 1 }, docId, newName, itemsMap, setItemsMap);
  if (!docId || !newName) return null;
  try {
    const updated = await apiRenameDoc(docId, newName);
    if (!updated) return null;
    const currentItem = itemsMap instanceof Map
      ? itemsMap.get(docMapKey(docId))
      : itemsMap?.[docMapKey(docId)];

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const newMap = new Map(itemsMap);
        newMap.set(docMapKey(docId), { ...currentItem, name: newName });
        setItemsMap(newMap);
      } else {
        const newObj = { ...itemsMap, [docMapKey(docId)]: { ...currentItem, name: newName } };
        setItemsMap(newObj);
      }
    }

    try {
      window.dispatchEvent(new CustomEvent('doc-renamed', {
        detail: {
          docId,
          name: newName,
          projectId,
          docType: updated?.docType || currentItem?.docType || null
        }
      }));
    } catch (err) {}

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return updated;
  } catch (err) {
    console.error('[docHelpers] renameDoc error', err);
    return null;
  }
};

export const updateDocLayout = async (docId, layout) => {
  debug(`updateDocLayout [doc:${docId}]`, { type: 'audit', level: 3 }, docId, layout);
  if (!docId) return null;
  try {
    const updated = await apiUpdateDocLayout(docId, layout);
    return updated;
  } catch (err) {
    console.error('[docHelpers] updateDocLayout error', err);
    return null;
  }
};

export const updateDocMetadata = async (docId, metadata) => {
  debug('updateDocMetadata', { type: 'audit', level: 3 }, docId, metadata);
  if (!docId) return null;
  try {
    const updated = await apiUpdateDocMetadata(docId, metadata);
    return updated;
  } catch (err) {
    console.error('[docHelpers] updateDocMetadata error', err);
    return null;
  }
};

export const moveDoc = async (docId, newParentId, itemsMap, setItemsMap, projectId = null, userId = null) => {
  if (!docId) return null;
  try {
    const moved = await apiMoveDoc(docId, newParentId);
    if (!moved) return null;

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const cur = itemsMap.get(docMapKey(docId));
        const newMap = new Map(itemsMap);
        newMap.set(docMapKey(docId), { ...cur, parent_id: newParentId });
        setItemsMap(newMap);
      } else {
        const cur = itemsMap[docMapKey(docId)];
        const newObj = { ...itemsMap, [docMapKey(docId)]: { ...cur, parent_id: newParentId } };
        setItemsMap(newObj);
      }
    }

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return moved;
  } catch (err) {
    console.error('[docHelpers] moveDoc error', err);
    return null;
  }
};

export const deleteDoc = async (docId, projectId, itemsMap, setItemsMap, tabs, setTabs, userId = null) => {
  debug(`deleteDoc [doc:${docId}]`, { type: 'audit', level: 1 }, docId, projectId, itemsMap, setItemsMap);
  if (!Array.isArray(tabs)) {
    console.error('[deleteDoc] tabs is not an array', tabs);
    return false;
  }

  try {
    const hasProjectId = projectId !== null && projectId !== undefined && projectId !== '';
    const normalizedProjectId = hasProjectId ? String(projectId) : null;
    const projectReferences = await apiGetProjectsForDoc(docId);
    const hasCurrentProjectReference = hasProjectId
      ? (projectReferences || []).some(ref => String(ref.project_id) === normalizedProjectId)
      : false;
    const referencesInOtherProjects = hasProjectId
      ? (projectReferences || []).some(ref => String(ref.project_id) !== normalizedProjectId)
      : (projectReferences || []).length > 0;
    const willDeleteDoc = !referencesInOtherProjects;

    if (willDeleteDoc) {
      try {
        const versions = await getVersions({ projectId, docId }).catch(() => []);
        for (const version of versions || []) {
          const versionId = version?.versionId;
          if (!versionId) continue;
          await deleteVersion({
            projectId,
            docId,
            versionId,
            skipImageReconcile: true,
            skipStorageAudit: true
          });
        }
      } catch (err) {
        console.warn(`[deleteDoc] failed to delete versions for doc ${docId}:`, err);
      }

      try {
        await reconcileOrphanImages({
          projectId,
          docId,
          keepImageUrls: [],
          purgeRetentionMs: 0,
          getVersions
        });
      } catch (err) {
        console.warn(`[deleteDoc] failed to reconcile images for doc ${docId}:`, err);
      }
    }

    if (willDeleteDoc) {
      try {
        await apiDeleteDocChunks(docId);
      } catch (err) {
        console.warn(`[deleteDoc] failed to delete chunks for doc ${docId}:`, err);
      }
    }

    let removedFromProject = false;
    if (hasCurrentProjectReference && hasProjectId) {
      removedFromProject = !!(await apiRemoveDoc(projectId, docId));
    }

    if (willDeleteDoc) {
      const shouldFallbackDelete = !hasProjectId || !hasCurrentProjectReference || !removedFromProject;
      if (shouldFallbackDelete) {
        try {
          await apiDeleteDoc(docId);
        } catch (err) {
          console.warn(`[deleteDoc] failed to delete doc ${docId}:`, err);
        }
      }
    }

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const newMap = new Map(itemsMap);
        newMap.delete(docMapKey(docId));
        setItemsMap(newMap);
      } else {
        const newObj = { ...itemsMap };
        delete newObj[docMapKey(docId)];
        setItemsMap(newObj);
      }
    }

    const remainingTabs = tabs.filter(t => t.docId !== docId);
    setTabs(remainingTabs);

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return true;
  } catch (err) {
    console.error('[deleteDoc] error', err);
    return false;
  }
};

export const getDocs = async (projectId) => {
  if (!projectId) return [];
  try {
    const docs = await apiGetDocs(projectId);
    return docs.map(d => ({
      id: d.id,
      name: d.name ?? 'Untitled Doc',
      parent_id: d.parent_id ?? null,
      type: 'doc',
      docType: d.docType ?? null,
      updated_at: d.updated_at ?? null
    }));
  } catch (err) {
    console.error('[docHelpers] getDocs error', err);
    return [];
  }
};

/**
 * Update remotely changed docs in the editor and tabs
 * @param {Array<number>} updatedDocIds - Array of doc IDs that were updated remotely
 * @param {Object} editorPanelRef - Reference to EditorPanel component
 * @param {Array} tabs - Current tabs array
 * @param {Function} setTabs - Function to update tabs
 * @param {Object} itemsMap - Project items map
 */
export const remoteDocsUpdate = async (updatedDocIds, editorPanelRef, tabs, setTabs, itemsMap) => {
  debug('remoteDocsUpdate', { type: 'audit', level: 1 }, { updatedDocIds, tabCount: tabs.length });
  
  if (!updatedDocIds || updatedDocIds.length === 0) return;
  if (!editorPanelRef?.current) return;
  
  // Get current editor states to check timestamps
  const editorStates = editorPanelRef.current.getEditorStates?.() || {};
  const normalizedUpdatedDocIds = updatedDocIds
    .map(id => String(id))
    .filter(id => id && id !== 'null' && id !== 'undefined');
  
  for (const docId of normalizedUpdatedDocIds) {
    try {
      // Load the remote doc content
      const remoteData = await loadDocContent(docId);
      
      // Check if doc is currently open in a tab
      const openTab = tabs.find(t => String(t.docId) === docId);
      
      if (openTab) {
        // Check if we have local state for this doc
        const localState = editorStates[docId] || editorStates[Number(docId)];
        const localSignature = createContentSignature(localState?.content || null);
        const remoteSignature = createContentSignature(remoteData?.content || null);
        const contentUnchanged = Boolean(localSignature) && Boolean(remoteSignature) && localSignature === remoteSignature;
        if (contentUnchanged) {
          debug('remoteDocsUpdate.skip', { type: 'log' }, `docId=${docId} reason=content-unchanged`);
          continue;
        }
        
        // Only update if remote is newer than local
        if (localState?.updated_at && remoteData.updated_at) {
          const localTime = new Date(localState.updated_at).getTime();
          const remoteTime = new Date(remoteData.updated_at).getTime();
          
          if (remoteTime <= localTime && contentUnchanged) {
            debug('remoteDocsUpdate.skip', { type: 'audit', level: 2 }, { 
              docId, 
              reason: 'local is newer or same',
              localTime,
              remoteTime
            });
            continue; // Skip if local is same age or newer
          }
        }
        
        // Update editor content if doc is open
        if (editorPanelRef.current.updateDocContent) {
          editorPanelRef.current.updateDocContent(docId, remoteData.content, remoteData.updated_at);
        }
        
        // Update tab label if name changed
        if (remoteData.name && openTab.label !== remoteData.name) {
          setTabs(prevTabs => 
            prevTabs.map(t => 
              t.docId === docId 
                ? { ...t, label: remoteData.name }
                : t
            )
          );
        }
      }
      
      // Update itemsMap if doc name changed
      if (itemsMap && remoteData.name) {
        const isMap = itemsMap instanceof Map;
        const currentItem = isMap ? itemsMap.get(docMapKey(docId)) : itemsMap[docMapKey(docId)];
        
        if (currentItem && currentItem.name !== remoteData.name) {
          debug('remoteDocsUpdate.updateItemsMap', { type: 'audit', level: 2 }, { 
            docId, 
            oldName: currentItem.name, 
            newName: remoteData.name 
          });
          // Note: itemsMap update should be handled by refresh() in App.js
          // which is called when projectUpdated is true
        }
      }
    } catch (err) {
      console.error(`[docHelpers] remoteDocsUpdate error for doc ${docId}:`, err);
    }
  }
};
